import { ContactType } from '@api/generated/graphql';
import { CustomerBySearchType } from '../../../../data/types';
import CustomerSearchResultInfoItem from '../CustomerSearchResultInfoItem/CustomerSearchResultInfoItem';
import ContactMethodsMenu from '../ContactMethodsMenu/ContactMethodsMenu';
import SearchResultBadge from '../SearchResultBadge';
import getContactMethodIconName from '@lib/utils/getContactMethodIconName';

interface SearchResultContactMethodsProps {
  contacts: CustomerBySearchType['contacts'];
  compact?: boolean;
}

/**
 * Renders the contact methods for a customer search result item.
 * If compact is true, only one contact method will be displayed, with a badge indicating the number of contact methods.
 * If compact is false, it will display 1 phone and 1 email contact method.
 *
 * @param contacts - The array of contact methods for the customer.
 * @param compact - A boolean indicating whether to display the contact methods in compact mode.
 */
const SearchResultContactMethods = ({
  contacts,
  compact,
}: SearchResultContactMethodsProps) => {
  const customerPhoneCount = contacts.filter(
    (cm) =>
      cm.type === ContactType.Phone || cm.type === ContactType.MobilePhone,
  ).length;
  const customerPhone = contacts.find(
    (cm) =>
      cm.type === ContactType.Phone || cm.type === ContactType.MobilePhone,
  );

  const customerEmailCount = contacts.filter(
    (cm) => cm.type === ContactType.Email,
  ).length;
  const customerEmail = contacts.find((cm) => cm.type === ContactType.Email);

  if (contacts.length === 0) {
    return null;
  }

  if (!compact) {
    return (
      <>
        {customerPhone && (
          <CustomerSearchResultInfoItem
            iconName={getContactMethodIconName(customerPhone.type)}
            content={customerPhone}
            badgeCount={customerPhoneCount - 1}
          />
        )}
        {customerEmail && (
          <CustomerSearchResultInfoItem
            iconName={getContactMethodIconName(customerEmail.type)}
            content={customerEmail}
            badgeCount={customerEmailCount - 1}
          />
        )}
      </>
    );
  }

  // compact mode only displays one contact method, so we prioritize phone number over email, and then the first contact method in the list
  const compactContact = customerPhone || customerEmail || contacts[0];

  return (
    <CustomerSearchResultInfoItem
      iconName={getContactMethodIconName(compactContact.type)}
      content={compactContact}
      renderBadge={
        <ContactMethodsMenu
          contacts={contacts}
          trigger={<SearchResultBadge count={contacts.length - 1} />}
        />
      }
    />
  );
};

export default SearchResultContactMethods;
